import { type FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ActionButton } from "widgets";

import { FormWarning } from "@/ui-modules/core/form/layout/FormWarning.js";

import {
  type ICombinedRegistrationNgsPlaybookConfig,
  type IConfiguredPlaybook,
  isNgsPlaybook,
} from "../../../app-utils/playbook.js";
import {
  createHiddenAttributesWarning,
  createLockedSamplesWarning,
  getCollidingSamplesWarning,
  isAttributePartOfEditingGrid,
} from "../../../app-utils/sample.js";
import { pluralizeWord } from "../../../app-utils/string.js";
import { type Attribute, type EditSamplesGrid, type InputTableQuerySpec, type SampleType } from "../../../graphql.js";
import { type IEditable } from "../../../redux/modules/data-editing.js";
import { getOrderedListOfTrackerAndWorkflowAttributes } from "../../../redux/modules/playbook-execution.js";
import {
  type IAttributesTemplatesLookup,
  loadSampleDataFromAnnotationColumns,
  loadSpecificSampleData,
  uniquenessValidations,
  updateSampleEditingState,
} from "../../../redux/modules/sample-editing.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import { ModalActionWidget } from "../../core/overlays/index.js";
import { useCountInvalidCells } from "../phenotype/EditingGrid.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";
import SampleEditingGrid, { validateNgsDataFiles } from "./SampleEditingGrid.js";
import SelectSamplesDialog from "./SelectSamplesDialog.js";

interface ICombinedRegistrationNgsExistingSampleGridScreenOwnProps extends IPlaybookWizardStepProvidedProps {
  disableChangeNumberOfSamples?: boolean;
}

/**
 * Playbook Screen for selecting already registered samples for NGS Analysis
 *
 */

const CombinedRegistrationNgsExistingSampleGridScreenImpl: FC<
  ICombinedRegistrationNgsExistingSampleGridScreenOwnProps
> = (props: ICombinedRegistrationNgsExistingSampleGridScreenOwnProps & IPlaybookWizardStepInjectedProps) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [validating, setValidating] = useState<boolean>(false);
  const [collidingSampleDataMessage, setCollidingSampleDataMessage] = useState<string>(null);

  const attributes = useSelector<IAppState, Attribute[]>((state) => state.sampleEditingState.sampleAttributes);
  const allAttributes = useSelector<IAppState, Attribute[]>(
    (state) => state.sampleEditingState.sampleType.sampleAttributes,
  );
  const sampleEditingTableQuerySpec = useSelector<IAppState, InputTableQuerySpec>(
    (state) => state.sampleEditingState.sampleEditingTableQuerySpec,
  );
  const selectedSampleType = useSelector<IAppState, SampleType>((state) => state.sampleEditingState.sampleType);
  const playbook = useSelector<IAppState, IConfiguredPlaybook>((state) => state.playbookExecution.playbook);
  const editingGrid = useSelector<IAppState, IEditable[]>((state) => state.sampleEditingState.editingGrid);
  const templatesLookup = useSelector<IAppState, IAttributesTemplatesLookup>(
    (state) =>
      state.sampleEditingState.sampleType &&
      state.sampleEditingState.templatesLookup[state.sampleEditingState.sampleType.id],
  );

  // setOverwriteWarning: check if data will be overwritten by exporting rules
  useEffect(() => {
    // get the tableColumns for the attributes mapped to exported fields
    const config = playbook?.config as ICombinedRegistrationNgsPlaybookConfig;
    const mappedAttributes: Attribute[] = config.ngsWorkflow.workflowExportMappings
      //.filter((workflowExportMapping) => workflowExportMapping.sampleAttributeId) // THIS IS PROBABLY NOT NECESSARY, to be tested without this line
      .map((workflowExportMapping) => allAttributes.find((a) => a.id === workflowExportMapping.sampleAttributeId))
      .filter((attribute) => !!attribute); // make sure the configured attribute is still applicable/available as part of the sample type
    // const mappedAttributes: Array<IAttribute> = this.props.allAttributes.filter((a) => a.tableColumn.dynamic);

    if (!sampleEditingTableQuerySpec || !mappedAttributes?.length) {
      setCollidingSampleDataMessage(null);
      return;
    }

    // load the sample data for the corresponding table columns, and check if any of those have non-empty value lists
    dispatch(
      loadSpecificSampleData(
        sampleEditingTableQuerySpec,
        mappedAttributes.map((a) => a.id),
      ),
    ).then((samples: EditSamplesGrid) => {
      setCollidingSampleDataMessage(getCollidingSamplesWarning(samples.samples, mappedAttributes));
    });
  }, [sampleEditingTableQuerySpec]);

  // data loading,  case of initial pre-selection of samples in context of a grid (e.g. group of samples)
  useEffect(() => {
    if (!sampleEditingTableQuerySpec) {
      return;
    }

    // load editing data
    const mandatoryAttributeIds: number[] = [];

    if (isNgsPlaybook(playbook)) {
      mandatoryAttributeIds.push(
        ...(playbook.config.ngsWorkflow?.workflowImportMappings?.map((mapping) => mapping.attributeId) || []),
      );
    }

    mandatoryAttributeIds.push(
      ...(playbook.config.sampleInput.filter((i) => i.correctedMandatory).map((i) => i.trackerAttributeId) || []),
    );

    dispatch(
      loadSampleDataFromAnnotationColumns(selectedSampleType.id, sampleEditingTableQuerySpec, mandatoryAttributeIds),
    ).catch((error: Error) => {
      props.updateWizardMessage(error.message);
    });
  }, [sampleEditingTableQuerySpec]);

  useEffect(() => {
    props.updateWizardMessage(!editingGrid.length ? "Choose one or more samples to use with this NGS analysis" : null);
  }, [editingGrid]);

  const onSubmit = () => {
    if (playbook.type.id === PLAYBOOK_TYPE.NGS_ANALYSIS_WITH_REGISTERED_SAMPLES) {
      const ngsDataFilesValidationError = validateNgsDataFiles(playbook, attributes, editingGrid);
      if (ngsDataFilesValidationError) {
        props.updateWizardMessage(ngsDataFilesValidationError);
        return;
      }
    }

    setValidating(true);

    dispatch(uniquenessValidations())
      .then(() => setValidating(false))
      .then(props.proceedWithNextStep)
      .catch((_error: Error) => {
        setValidating(false);
      });
  };

  // the selection  does not contain all the required data (e.g. stored only the identifiers or so) therefore we need to flesh out this selection with the available data
  const visibleAttributes: Attribute[] = useMemo(() => {
    // the selection  does not contain all the required data (e.g. stored only the identifiers or so) therefore we need to flesh out this selection with the available data
    return getOrderedListOfTrackerAndWorkflowAttributes(playbook.config.sampleInput, attributes).filter((a) =>
      isAttributePartOfEditingGrid(a, templatesLookup),
    );
  }, [attributes]);

  const invalidCellsCount: number = useCountInvalidCells(editingGrid);
  const multiValueWarning: string = createHiddenAttributesWarning(attributes);

  const lockedSamples: IEditable[] = editingGrid.filter((r) => r.locked);
  const lockedSamplesWarning: string | null = createLockedSamplesWarning(lockedSamples?.length);

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
        description:
          "Choose one or more samples from the database to use with this NGS analysis. The results from the analysis will be associated with the samples that you select.",
      }}
      wizardFooterData={{
        error:
          invalidCellsCount > 0
            ? `${invalidCellsCount} ${pluralizeWord("cell", invalidCellsCount)} ${pluralizeWord(
                "is",
                invalidCellsCount,
              )} invalid.`
            : null,
        enableSubmissionUponWizardError: true,
        warning: collidingSampleDataMessage,
        nextButtonProps: {
          label: "Next",
          isLoading: validating,
          onPress: onSubmit,
          isDisabled: invalidCellsCount > 0 || !editingGrid.length,
        },
      }}
    >
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", rowGap: 15 }}>
        <div>
          <ModalActionWidget
            actionWidget={({ open }) => <ActionButton onPress={() => open()}>Select Samples</ActionButton>}
          >
            {({ close }) => (
              <SelectSamplesDialog
                setSampleEditingSpec={(tableSpec: InputTableQuerySpec) =>
                  dispatch(updateSampleEditingState({ sampleEditingTableQuerySpec: tableSpec }))
                }
                initialSelection={editingGrid.map((row) => row.id as undefined as number)}
                sampleType={selectedSampleType}
                close={close}
              />
            )}
          </ModalActionWidget>
        </div>

        {multiValueWarning && <FormWarning>{multiValueWarning}</FormWarning>}
        {lockedSamplesWarning && <FormWarning>{lockedSamplesWarning}</FormWarning>}
        <SampleEditingGrid attributes={visibleAttributes} templatesLookup={templatesLookup} />
      </div>
    </PlaybookWizardStep>
  );
};

export default CombinedRegistrationNgsExistingSampleGridScreenImpl;
